import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TYPE_COLORS = {
  evenement: "bg-cef-blue text-white",
  mobilisation: "bg-cef-pink text-white",
  outil: "bg-cef-yellow text-black",
};

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

/**
 * Monthly calendar view for actions. Click a day with events to navigate
 * to the first event's detail page. Click a chip to navigate to that event.
 */
export default function ActionsCalendar({ items, getHref = (item) => `/actions/${item.id}` }) {
  const [cursor, setCursor] = React.useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const byDay = React.useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (!it.date) return;
      const k = it.date.slice(0, 10); // YYYY-MM-DD
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return map;
  }, [items]);

  // Build a grid of weeks starting on Monday
  const grid = React.useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    // JS getDay(): 0=Sun,1=Mon,…,6=Sat → offset to Monday-start
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, month, d));
    }
    while (cells.length % 7) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [cursor]);

  const today = new Date();
  const isToday = (d) => d && d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  const isoOf = (d) => d.toISOString().slice(0, 10);

  return (
    <div className="neo-card p-4 sm:p-5 bg-white" data-testid="actions-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
          className="neo-btn bg-white text-sm py-2"
          data-testid="cal-prev"
        ><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display font-black text-lg sm:text-xl uppercase tracking-tight" data-testid="cal-title">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </div>
        <button
          onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
          className="neo-btn bg-white text-sm py-2"
          data-testid="cal-next"
        ><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase font-bold text-neutral-500 mb-1">
        {DAYS.map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.flat().map((d, i) => {
          if (!d) return <div key={`empty-${i}`} className="aspect-square sm:aspect-[5/4]" />;
          const key = isoOf(d);
          const evs = byDay.get(key) || [];
          return (
            <div
              key={key}
              className={`aspect-square sm:aspect-[5/4] border-2 border-black/20 rounded-md p-1 text-[10px] overflow-hidden ${isToday(d) ? "bg-cef-yellow border-black" : ""}`}
              data-testid={`cal-day-${key}`}
            >
              <div className="font-bold text-neutral-700">{d.getDate()}</div>
              <div className="mt-0.5 space-y-0.5">
                {evs.slice(0, 2).map((e) => (
                  <Link
                    key={e.id}
                    to={getHref(e)}
                    className={`block truncate px-1 py-0.5 rounded text-[9px] font-bold ${TYPE_COLORS[e.type] || "bg-black text-white"}`}
                    title={e.title || e.name}
                    data-testid={`cal-event-${e.id}`}
                  >
                    {e.title || e.name}
                  </Link>
                ))}
                {evs.length > 2 && <div className="text-[9px] text-neutral-500">+{evs.length - 2}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
